import { useCallback } from 'react';

import { useCreatePayoutAccount } from '@givebacks/api/core';
import { REGEX } from '@givebacks/utils';
import { DateTime } from 'luxon';
import {
  ButtonMantine,
  Center,
  DatePickerInput,
  Grid,
  Group,
  ImageMantine,
  Select,
  Stack,
  Text,
  TextInput,
  notifications,
} from 'ui';
import { UseFormReturnType, isEmail, isNotEmpty, matches, useForm } from 'ui/Form';
import { svg_hyperwallet } from 'ui/Icons';

type UseCreateHyperWalletAccountForm = UseFormReturnType<CreateHyperWalletAccountForm>;

type CreateHyperWalletAccountForm = {
  first_name: string;
  last_name: string;
  email: string;
  address1: string;
  address2: string;
  country: string;
  state: string;
  city: string;
  zip_code: string;
  date_of_birth: Date | null;
};

type HyperwalletUserData = {
  email: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  country: string;
  state: string;
  address1: string;
  address2: string;
  city: string;
  zip_code: string;
};

type CreateHyperWalletAccountProps = {
  onCreateAccount: () => void;
};

const CreateHyperWalletAccount = ({ onCreateAccount }: CreateHyperWalletAccountProps) => {
  const { mutate: connectHyperWallet, isPending: isLoading } = useCreatePayoutAccount();

  const form = useForm<CreateHyperWalletAccountForm>({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      address1: '',
      address2: '',
      city: '',
      country: '',
      zip_code: '',
      state: '',
      date_of_birth: null,
    },
    validate: {
      first_name: (value) => (value.trim().length === 0 ? 'First name is required' : null),
      last_name: (value) => (value.trim().length === 0 ? 'Last name is required' : null),
      email: isEmail('Email is required'),
      address1: matches(REGEX.ADDRESS1, 'Address is invalid'),
      city: matches(REGEX.CITY, 'City is invalid'),
      country: (value) => (value.trim().length === 0 ? 'Country is required' : null),
      zip_code: matches(REGEX.ZIP_CODE, 'Zip code is invalid'),
      state: isNotEmpty('State is required'),
      date_of_birth: isNotEmpty('Date of Birth is required'),
    },
  });

  const handleSubmitForm = useCallback(() => {
    const { hasErrors } = form.validate();
    if (hasErrors || !form.values.date_of_birth) {
      return;
    }

    const requestObject: HyperwalletUserData = {
      ...form.values,
      date_of_birth: DateTime.fromJSDate(form.values.date_of_birth).toUTC().toString(),
    };
    connectHyperWallet(
      {
        accountType: 'users',
        request: { user: { ...requestObject } },
      },
      {
        onSuccess() {
          onCreateAccount();
          notifications.show({
            title: 'Account Connected',
            message: 'Your account has been connected',
            variant: 'success',
          });
        },
        onError() {
          notifications.show({
            title: 'Error',
            message: 'There was an error connecting your account',
            variant: 'error',
          });
        },
      }
    );
  }, [connectHyperWallet, form, onCreateAccount]);

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Text fz="sm" c="gray">
          To connect your accounts to Givebacks, you need to first confirm your identity.
        </Text>
        <Center>
          <Group gap="xs">
            <Text fz={10}>Secured with</Text>
            <ImageMantine src={svg_hyperwallet} h={22} w="auto" />
          </Group>
        </Center>
      </Stack>

      <Grid gutter="xs">
        <Grid.Col span={6}>
          <TextInput {...form.getInputProps('first_name')} placeholder="First Name" />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput {...form.getInputProps('last_name')} placeholder="Last Name" />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput {...form.getInputProps('email')} placeholder="Email" />
        </Grid.Col>
        <Grid.Col span={12}>
          <DatePickerInput
            placeholder={`Date of Birth`}
            {...form.getInputProps('date_of_birth')}
            maxDate={new Date()}
            leftSection={null}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput {...form.getInputProps('address1')} placeholder="Address" />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput {...form.getInputProps('address2')} placeholder="Address 2" />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput {...form.getInputProps('city')} placeholder="City" />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput {...form.getInputProps('zip_code')} placeholder="Zip Code" />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select.Countries {...form.getInputProps('country')} placeholder="Country" />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select.States {...form.getInputProps('state')} placeholder="State" />
        </Grid.Col>
      </Grid>
      <ButtonMantine onClick={handleSubmitForm} loading={isLoading}>
        Confirm
      </ButtonMantine>
    </Stack>
  );
};

export { CreateHyperWalletAccount };
export type { UseCreateHyperWalletAccountForm };

